import React, { createContext, useContext, ReactNode } from 'react';
import { useApplicationSettings, HomeScreenOption, PageId } from '../hooks/useApplicationSettings';

interface ApplicationSettingsContextType {
  homeScreen: HomeScreenOption;
  disabledPages: PageId[];
  isLoading: boolean;
  error: string | null;
  updateHomeScreen: (value: HomeScreenOption) => Promise<void>;
  updateDisabledPages: (pages: PageId[]) => Promise<void>;
  isPageDisabled: (pageId: PageId) => boolean;
  refreshSettings: () => Promise<void>;
}

// Create context with default values
const ApplicationSettingsContext = createContext<ApplicationSettingsContextType>({
  homeScreen: 'location_calendar',
  disabledPages: [],
  isLoading: true,
  error: null,
  updateHomeScreen: async () => {},
  updateDisabledPages: async () => {},
  isPageDisabled: () => false,
  refreshSettings: async () => {},
});

// Hook to use the application settings context
export const useApplicationSettingsContext = () => useContext(ApplicationSettingsContext);

// Provider component
export const ApplicationSettingsProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { settings, isLoading, error, updateSettings, loadSettings } = useApplicationSettings();

  // Function to update home screen setting
  const updateHomeScreen = async (value: HomeScreenOption) => {
    await updateSettings({ home_screen: value });
  };

  // Function to update disabled pages
  const updateDisabledPages = async (pages: PageId[]) => {
    await updateSettings({ disable_pages: pages });
  };

  // Function to check if a page is disabled
  const isPageDisabled = (pageId: PageId): boolean => {
    return settings.disable_pages?.includes(pageId) || false;
  };

  // Function to refresh settings
  const refreshSettings = async () => {
    return loadSettings();
  };


  return (
    <ApplicationSettingsContext.Provider 
      value={{
        homeScreen: settings.home_screen,
        disabledPages: settings.disable_pages || [],
        isLoading,
        error,
        updateHomeScreen,
        updateDisabledPages,
        isPageDisabled,
        refreshSettings
      }}
    >
      {children}
    </ApplicationSettingsContext.Provider>
  );
}; 